import { useEffect, useState } from "react";
import { useMembershipStore } from "features/ManageOfferings/modules/MembershipsSection/store/useMembershipStore";
import { checkIsObjectEmpty } from "features/Common/modules/DataTypes/utils/object";
import { useMembership } from "features/ManageOfferings/modules/MembershipsSection/utils/hooks/useMembership";
import { useOfferingVariationListStore } from "../../../../store/useOfferingVariationList.store";
import { MEMBERSHIP_OFFERING_PRICE_CHOICES } from "ui/pages/ManageBrandedCommunity/components/AddBonusOfferingModal/constants/AddBonusOfferingModal.constants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { CHANGE_STATES } from "features/Common/modules/Form/constants/Form.constants";
import { is_empty } from "features/Common/utils/common.utils";
import { colomnKeys } from "../../../../OfferingVariationsList.constants";
import { DEFAULT_MEMBERSHIP_LIST_SIZE } from "features/ManageOfferings/modules/MembershipsSection/constants/MembershipsSection.data";
import { FORM_FIELDS, NULL_PRICE } from "../../AddVariation.constants";
import { isInteger } from "utils/validations";

export const useAddVariations = () => {
  const { notify } = useNotifications();
  const { getMembershipList } = useMembership();
  const offeringUuid = useOfferingVariationListStore(
    (state) => state.offeringUuid
  );
  const showInternationalPrice = useOfferingVariationListStore(
    (state) => state.showInternationalPrice
  );
  const selectedVariation = useOfferingVariationListStore(
    (state) => state.selectedVariation
  );
  const formData = useOfferingVariationListStore((state) => state.formData);
  const setFormData = useOfferingVariationListStore(
    (state) => state.setFormData
  );
  const setIsMembership = useOfferingVariationListStore(
    (state) => state.setIsMembership
  );

  const setMembershipListMap = useOfferingVariationListStore(
    (state) => state.setMembershipListMap
  );
  const variantPricesByMembership = useOfferingVariationListStore(
    (state) => state.variantPricesByMembership
  );
  const setVariantPricesByMembership = useOfferingVariationListStore(
    (state) => state.setVariantPricesByMembership
  );
  const defaultMembershipId = useOfferingVariationListStore(
    (state) => state.defaultMembershipId
  );
  const setDefaultMembershipId = useOfferingVariationListStore(
    (state) => state.setDefaultMembershipId
  );
  const membershipList = useMembershipStore((state) => state.membershipList);
  const [errorObj, setErrorObj] = useState({
    domesticPrice: false,
    internationalPrice: false,
  });

  useEffect(() => {
    if (offeringUuid) {
      getMembershipList({ listingUuid: offeringUuid });
    }
  }, [offeringUuid]);

  useEffect(() => {
    // determine whether membership-specific pricing should be enabled (only when multiple memberships exist)
    setIsMembership(
      !checkIsObjectEmpty(membershipList) &&
        membershipList?.list &&
        membershipList?.listSize > DEFAULT_MEMBERSHIP_LIST_SIZE
    );
    if (!checkIsObjectEmpty(membershipList) && membershipList?.list) {
      // ----- MULTIPLE MEMBERSHIPS FLOW -----
      if (membershipList?.listSize > DEFAULT_MEMBERSHIP_LIST_SIZE) {
        setDefaultMembershipId(null);
        // Map membership data to id/label format for accordion rendering.
        setMembershipListMap(
          membershipList?.list.map((membership) => {
            return {
              id: membership?.uuid,
              label: membership?.title,
            };
          })
        );
        // Initialize membership values for variantions
        if (!selectedVariation || is_empty(selectedVariation)) {
          setVariantPricesByMembership(
            membershipList?.list.map((membership) => {
              return {
                id: membership?.uuid,
                selectedValue:
                  MEMBERSHIP_OFFERING_PRICE_CHOICES.AT_DISCOUNTED_PRICE,
                discountedPrice: null,
                enableInternationalPrice: false,
                internationalPrice: null,
              };
            })
          );
        } else {
          // hydrate base form prices when editing an existing variation
          setFormData({
            ...formData,
            [colomnKeys.price.domestic_price]:
              selectedVariation?.domestic_price,
            [colomnKeys.price.international_price]:
              selectedVariation?.international_price,
          });
        }
        // ----- SINGLE MEMBERSHIP FLOW -----
      } else if (membershipList?.listSize === DEFAULT_MEMBERSHIP_LIST_SIZE) {
        // set single membership as default and disable membership-specific pricing UI
        setDefaultMembershipId(membershipList?.list[0]?.uuid);
        setMembershipListMap([]);
        setVariantPricesByMembership([]);
        // populate pricing fields when editing an existing variation
        if (selectedVariation && !is_empty(selectedVariation)) {
          setFormData({
            ...formData,
            [colomnKeys.price.domestic_price]:
              selectedVariation?.domestic_price,
            [colomnKeys.price.international_price]:
              selectedVariation?.international_price,
          });
        }
      }
    }
  }, [membershipList, selectedVariation]);

  const getMembershipPriceFieldsById = ({ fieldKey, membershipId }) => {
    const filteredValue = variantPricesByMembership.find((segmentPrice) => {
      return segmentPrice?.id === membershipId;
    });
    if (filteredValue && !checkIsObjectEmpty(filteredValue)) {
      return filteredValue?.[fieldKey];
    } else {
      return null;
    }
  };
  const selectedOption = ({ optionKey, membershipId = null }) => {
    if (optionKey && membershipId) {
      const filteredValue = variantPricesByMembership.find((segmentPrice) => {
        return segmentPrice?.id === membershipId;
      });
      if (filteredValue && !checkIsObjectEmpty(filteredValue)) {
        return filteredValue?.selectedValue === optionKey;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };
  const handleOptionChange = ({ value, membershipId = null }) => {
    if (membershipId) {
      let updatedVariantPricesByMembership = [...variantPricesByMembership];
      const filteredValueIndex = variantPricesByMembership.findIndex(
        (segmentPrice) => {
          return segmentPrice?.id === membershipId;
        }
      );
      if (filteredValueIndex != -1) {
        updatedVariantPricesByMembership[filteredValueIndex].selectedValue =
          value;
        updatedVariantPricesByMembership[filteredValueIndex].discountedPrice =
          null;
        updatedVariantPricesByMembership[
          filteredValueIndex
        ].enableInternationalPrice = false;
        updatedVariantPricesByMembership[
          filteredValueIndex
        ].internationalPrice = null;
        setVariantPricesByMembership([...updatedVariantPricesByMembership]);
      }
    }
  };

  const handleValueChange = ({ value, fieldKey, membershipId = null }) => {
    if (membershipId) {
      let updatedVariantPricesByMembership = [...variantPricesByMembership];
      const filteredValueIndex = variantPricesByMembership.findIndex(
        (segmentPrice) => {
          return segmentPrice?.id === membershipId;
        }
      );
      if (filteredValueIndex != -1) {
        updatedVariantPricesByMembership[filteredValueIndex][fieldKey] = value;
      }
      setVariantPricesByMembership([...updatedVariantPricesByMembership]);
    }
  };

  const onEnableInternationalPriceClick = ({ value, membershipId = null }) => {
    if (membershipId) {
      let updatedVariantPricesByMembership = [...variantPricesByMembership];
      const filteredValueIndex = variantPricesByMembership.findIndex(
        (segmentPrice) => {
          return segmentPrice?.id === membershipId;
        }
      );
      if (filteredValueIndex != -1) {
        updatedVariantPricesByMembership[
          filteredValueIndex
        ].enableInternationalPrice = value;
        if (!value) {
          updatedVariantPricesByMembership[
            filteredValueIndex
          ].internationalPrice = null;
          setErrorObj({ ...errorObj, internationalPrice: false });
        }
        setVariantPricesByMembership([...updatedVariantPricesByMembership]);
      }
    }
  };

  const validatePrice = ({ price }) => {
    if ((String(price) || "").trim() === "") return "Required";
    if (!isInteger(parseFloat(price))) return "Invaild Price";
    let _price = Number(price || NULL_PRICE);
    if (!(_price > 0)) return "Price should be greater than 0";

    return "";
  };

  const validateRequestData = ({ variationData }) => {
    let _domesticPrice = variationData?.discountedPrice
      ? variationData?.discountedPrice
      : NULL_PRICE;
    let _internationalPrice = variationData?.internationalPrice
      ? variationData?.internationalPrice
      : NULL_PRICE;
    if (
      selectedOption({
        optionKey: MEMBERSHIP_OFFERING_PRICE_CHOICES.AT_DISCOUNTED_PRICE,
        membershipId: variationData?.id,
      })
    ) {
      if (validatePrice({ price: _domesticPrice, isInternational: false })) {
        notify("Please enter a valid price", notification_color_keys.error);
        setErrorObj({ ...errorObj, domesticPrice: true });
        return false;
      }

      if (variationData?.enableInternationalPrice) {
        if (
          validatePrice({
            price: _internationalPrice,
            isInternational: true,
          })
        ) {
          notify(
            "Please enter a valid international price",
            notification_color_keys.error
          );
          setErrorObj({ ...errorObj, internationalPrice: true });
          return false;
        }
      }
    }
    return true;
  };

  const parsePayloadOnAddClick = () => {
    const payload = [];
    let isValid = true;
    if (
      variantPricesByMembership &&
      !checkIsArrayEmpty(variantPricesByMembership)
    ) {
      variantPricesByMembership.forEach((segmentPrice) => {
        if (!isValid) return;
        isValid = validateRequestData({ variationData: segmentPrice });

        if (
          segmentPrice?.selectedValue !==
          MEMBERSHIP_OFFERING_PRICE_CHOICES.NOT_INCLUDED
        ) {
          const variation_mapped_element = {
            change_type: CHANGE_STATES.added,
            membership_uuid: segmentPrice?.id,
            listing_uuid: offeringUuid,
            title: formData?.title,
            domestic_price:
              parseInt(segmentPrice?.discountedPrice) || NULL_PRICE,
            subscription_plan_magnitude: formData?.subscription_plan_magnitude,
            subscription_plan_choice: formData?.subscription_plan_choice,
          };
          if (segmentPrice?.enableInternationalPrice) {
            variation_mapped_element.international_price =
              parseInt(segmentPrice?.internationalPrice) || NULL_PRICE;
          }
          payload.push({ ...variation_mapped_element });
        }
      });
    } else {
      const variation_mapped_element = {
        change_type: CHANGE_STATES.added,
        membership_uuid: defaultMembershipId,
        listing_uuid: offeringUuid,
        title: formData?.title,
        domestic_price: parseInt(formData?.domestic_price) || NULL_PRICE,
        subscription_plan_magnitude: formData?.subscription_plan_magnitude,
        subscription_plan_choice: formData?.subscription_plan_choice,
      };
      if (showInternationalPrice) {
        variation_mapped_element.international_price =
          parseInt(formData?.[FORM_FIELDS.international_price.key]) ||
          NULL_PRICE;
      }
      payload.push({ ...variation_mapped_element });
    }

    return isValid
      ? {
          listing_uuid: offeringUuid,
          variant_membership_mappings: [...payload],
        }
      : false;
  };

  const parsePayloadOnEditClick = () => {
    const payload = [];

    const variation_mapped_element = {
      change_type: CHANGE_STATES.updated,
      uuid: selectedVariation?.uuid,
      membership_uuid: selectedVariation?.membership_details?.uuid,
      listing_uuid: offeringUuid,
      updated_values: {
        title: formData?.title,
        domestic_price: parseInt(formData?.domestic_price) || NULL_PRICE,
        subscription_plan_magnitude: formData?.subscription_plan_magnitude,
        subscription_plan_choice: formData?.subscription_plan_choice,
      },
    };
    variation_mapped_element.updated_values.international_price =
      showInternationalPrice
        ? parseInt(formData?.[FORM_FIELDS.international_price.key]) ||
          NULL_PRICE
        : NULL_PRICE;
    payload.push({ ...variation_mapped_element });

    return {
      listing_uuid: offeringUuid,
      variant_membership_mappings: [...payload],
    };
  };

  const parsePayloadOnDeletedClick = () => {
    const payload = [];

    const variation_mapped_element = {
      change_type: CHANGE_STATES.updated,
      uuid: selectedVariation?.uuid,
      membership_uuid: selectedVariation?.membership_details?.uuid,
      listing_uuid: offeringUuid,
      updated_values: {
        is_active: false,
      },
    };
    payload.push({ ...variation_mapped_element });

    return {
      listing_uuid: offeringUuid,
      variant_membership_mappings: [...payload],
    };
  };

  const handlePriceValueChange = ({ e, fieldKey, membershipId }) => {
    const value = e.target.value || "";
    handleValueChange({ value, fieldKey: fieldKey, membershipId });
    setErrorObj({
      ...errorObj,
      [fieldKey]: false,
    });
  };

  return {
    selectedOption,
    handleOptionChange,
    handleValueChange,
    onEnableInternationalPriceClick,
    validatePrice,
    getMembershipPriceFieldsById,
    errorObj,
    setErrorObj,
    parsePayloadOnAddClick,
    parsePayloadOnEditClick,
    parsePayloadOnDeletedClick,
    handlePriceValueChange,
  };
};
